"use client";

/**
 * P9-13 · SSR-safe media-query hook. Built on `useSyncExternalStore` so the
 * first client render already reflects the real viewport (no post-mount flash
 * of the wrong branch) while the server snapshot is a stable `false` — the
 * hydration-correct way to switch component trees on a breakpoint. Used to pick
 * the mobile one-page checkout below `md` without mounting both layouts (which
 * would double the PaymentIntent and the funnel events).
 */
import * as React from "react";

export function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      // Guard environments without matchMedia (jsdom under test) — nothing to
      // subscribe to, and the snapshot stays its default.
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );
  const getSnapshot = React.useCallback(
    () =>
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia(query).matches
        : false,
    [query],
  );
  // Server renders the desktop tree; the client corrects on first paint.
  const getServerSnapshot = React.useCallback(() => false, []);
  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** True below Tailwind's `md` (768px) breakpoint — i.e. the mobile layout. */
export function useIsBelowMd(): boolean {
  return useMediaQuery("(max-width: 767.98px)");
}
